import { Fragment } from 'react'
import { IconButton, InlineTitle } from '../../components/controls.js'
import { useUiStore } from '../../state/ui-store.js'
import { useBoardStore } from '../../state/board-store.js'
import { formatMinutes, useColumn, useEstimateTotals, useGroup } from '../../state/selectors.js'
import type { StoryMapActions } from './actions.js'
import { ArchitectureIcons } from '../context/ArchitectureIcons.js'
import { FindingsBadge } from '../findings/Findings.js'
import { HeadingNotesButton } from '../heading-notes/HeadingNotes.js'
import styles from './StoryMap.module.css'

interface Run {
  key: string
  groupId: string | null
  span: number
}

/** Contiguous columns sharing a group form one spanning header; separated runs repeat the label. */
function groupRuns(columnIds: readonly string[], groupOf: (columnId: string) => string | null): Run[] {
  const runs: Run[] = []
  for (const columnId of columnIds) {
    const groupId = groupOf(columnId)
    const last = runs.at(-1)
    if (last && last.groupId === groupId) {
      last.span += 1
      continue
    }
    runs.push({ key: `${groupId ?? 'none'}-${columnId}`, groupId, span: 1 })
  }
  return runs
}

export function GroupHeaderRow({ columnIds, actions }: { columnIds: readonly string[]; actions: StoryMapActions }) {
  const columns = useBoardStore((state) => state.entities?.columns)
  const runs = groupRuns(columnIds, (columnId) => columns?.[columnId]?.groupId ?? null)
  return (
    <>
      <div className={styles.corner} style={{ height: 'auto' }} />
      {runs.map((run) => (
        <GroupHeaderCell key={run.key} groupId={run.groupId} span={run.span} actions={actions} />
      ))}
      <div className={styles.headFiller} />
    </>
  )
}

function GroupHeaderCell({
  groupId,
  span,
  actions,
}: {
  groupId: string | null
  span: number
  actions: StoryMapActions
}) {
  const group = useGroup(groupId)
  if (!group) {
    return (
      <div className={`${styles.groupCell} ${styles.ungrouped}`} style={{ gridColumn: `span ${span}` }}>
        <span>Ungrouped</span>
      </div>
    )
  }
  return (
    <div className={styles.groupCell} style={{ gridColumn: `span ${span}` }} data-heading>
      <InlineTitle
        value={group.title}
        label={`Rename group ${group.title}`}
        onCommit={(title) => actions.renameGroup(group.id, title)}
      />
      <HeadingNotesButton scope="group" id={group.id} title={group.title} />
      <IconButton danger aria-label={`Delete group ${group.title}`} onClick={() => actions.deleteGroup(group.id)}>
        &#10005;
      </IconButton>
    </div>
  )
}

export function ColumnHeaderRow({
  columnIds,
  groupIds,
  actions,
}: {
  columnIds: readonly string[]
  groupIds: readonly string[]
  actions: StoryMapActions
}) {
  return (
    <>
      <div className={styles.corner} />
      {columnIds.map((columnId, index) => (
        <ColumnHeaderCell
          key={columnId}
          columnId={columnId}
          groupIds={groupIds}
          isFirst={index === 0}
          isLast={index === columnIds.length - 1}
          actions={actions}
        />
      ))}
      <div className={styles.headFiller}>
        <button type="button" className={styles.addCard} onClick={() => actions.addColumn()}>
          + Add column
        </button>
      </div>
    </>
  )
}

const NEW_GROUP = '__new__'

function ColumnHeaderCell({
  columnId,
  groupIds,
  isFirst,
  isLast,
  actions,
}: {
  columnId: string
  groupIds: readonly string[]
  isFirst: boolean
  isLast: boolean
  actions: StoryMapActions
}) {
  const column = useColumn(columnId)
  const groups = useBoardStore((state) => state.entities?.groups)
  const estimate = formatMinutes(useEstimateTotals().byColumn[columnId] ?? null)
  if (!column) return null

  return (
    <div className={styles.columnCell} data-heading>
      <div className={styles.titleWithIcons}>
        <ArchitectureIcons context={column.context} />
        <InlineTitle
          value={column.title}
          label={`Rename column ${column.title}`}
          onCommit={(title) => actions.renameColumn(columnId, title)}
        />
        <HeadingNotesButton scope="column" id={columnId} title={column.title} />
        <FindingsBadge targetId={columnId} label={`column ${column.title}`} />
      </div>
      <div className={styles.controls}>
        {/* Only when something in the column is estimated: an absent total is not zero. */}
        {estimate ? (
          <span className={styles.estimate} title={`Estimated ${estimate} in this column`}>
            {estimate}
          </span>
        ) : null}
        <IconButton
          aria-label={`Prompt preview for column ${column.title}`}
          title="Prompt preview"
          onClick={() => useUiStore.getState().setPromptScope({ kind: 'column', id: columnId })}
        >
          <span aria-hidden="true">&#10095;</span>
        </IconButton>
        <IconButton
          aria-label={`Architecture context for column ${column.title}`}
          title="Architecture context"
          onClick={() => useUiStore.getState().setContextOwner({ scope: 'column', id: columnId })}
        >
          <span aria-hidden="true">&#9671;</span>
        </IconButton>
      </div>
      <div className={styles.controls}>
        <IconButton
          aria-label={`Move column ${column.title} earlier`}
          disabled={isFirst}
          onClick={() => actions.moveColumn(columnId, 'earlier')}
        >
          &#9664;
        </IconButton>
        <IconButton
          aria-label={`Move column ${column.title} later`}
          disabled={isLast}
          onClick={() => actions.moveColumn(columnId, 'later')}
        >
          &#9654;
        </IconButton>
        <select
          className={styles.groupSelect}
          aria-label={`Group for column ${column.title}`}
          value={column.groupId ?? ''}
          onChange={(event) => {
            const value = event.target.value
            if (value === NEW_GROUP) {
              // Create and assign in one action; the name is edited in the group header.
              actions.addGroup('New group')
              const state = useBoardStore.getState().entities
              const created = state?.groupIds.at(-1)
              if (created) actions.setColumnGroup(columnId, created)
              return
            }
            actions.setColumnGroup(columnId, value === '' ? null : value)
          }}
        >
          <option value="">No group</option>
          {groupIds.map((groupId) => (
            <Fragment key={groupId}>
              <option value={groupId}>{groups?.[groupId]?.title ?? 'Group'}</option>
            </Fragment>
          ))}
          <option value={NEW_GROUP}>New group…</option>
        </select>
        <IconButton
          danger
          aria-label={`Delete column ${column.title}`}
          onClick={() => actions.deleteColumn(columnId)}
        >
          &#10005;
        </IconButton>
      </div>
    </div>
  )
}
